/**
 * Page admin "Alertes sécurité" — visualise les events poussés par SecurityMonitor + détectés backend.
 * Route : /admin/security
 *
 * - Stats (24h / 7d / total / by_kind / by_severity)
 * - Tableau filtrable des alertes
 * - Action delete (superadmin only)
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Shield, AlertTriangle, Search, Trash2, RefreshCw, Globe2, List, CheckCircle2, EyeOff, Layers } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import SecurityGeoMap from "../components/security/SecurityGeoMap";

const SEVERITY_BADGES = {
  low:      { color: "#64748b", bg: "bg-slate-500/15",   label: "LOW" },
  medium:   { color: "#f59e0b", bg: "bg-amber-500/15",   label: "MEDIUM" },
  high:     { color: "#f43f5e", bg: "bg-rose-500/15",    label: "HIGH" },
  critical: { color: "#dc2626", bg: "bg-red-600/20",     label: "CRITICAL" },
};

const KIND_LABELS = {
  devtools_open:    "🔍 DevTools ouvert",
  console_use:      "💻 Console utilisée",
  rapid_clicks:     "👆 Clics rapides",
  bot_user_agent:   "🤖 Bot UA",
  scrape_pattern:   "🕸️ Scrap robotique",
  auto_paste:       "📋 Collage auto",
  burst_requests:   "🚀 Burst API",
  auth_brute_force: "🔓 Brute-force auth",
  suspicious_export:"📥 Export suspect",
};

export default function SecurityAlertsPage() {
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <SecurityAlertsView />
    </div>
  );
}

export function SecurityAlertsView({ embedded = false }) {
  const { axiosAuth, user } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list"); // 'list' | 'map' | 'grouped'
  const [groups, setGroups] = useState([]);

  const isSuper = user?.role === "superadmin" && !user?.tenant_id;

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterKind) params.set("kind", filterKind);
      if (filterSeverity) params.set("severity", filterSeverity);
      if (filterCountry) params.set("country_code", filterCountry);
      if (showResolved) params.set("show_resolved", "true");
      const [a, s, g] = await Promise.all([
        api.get(`/security/alerts?${params}`),
        api.get("/security/alerts/stats"),
        api.get("/security/alerts/grouped?window_minutes=60"),
      ]);
      setItems(a.data?.items || []);
      setStats(s.data);
      setGroups(g.data?.groups || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filterKind, filterSeverity, filterCountry, showResolved]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (a) => a.user_email?.toLowerCase().includes(q) || a.kind?.includes(q) || a.ip?.includes(q),
    );
  }, [items, search]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette alerte ?")) return;
    await api.delete(`/security/alerts/${id}`);
    reload();
  };

  const resolve = async (id) => {
    await api.post(`/security/alerts/${id}/resolve`);
    reload();
  };

  const dismiss = async (id) => {
    await api.post(`/security/alerts/${id}/dismiss`);
    reload();
  };

  const bulkResolveGroup = async (ip, kind) => {
    await api.post("/security/alerts/bulk-resolve", { ip, kind });
    reload();
  };

  return (
    <div className={embedded ? "" : "p-6 max-w-7xl mx-auto"} data-testid="security-alerts-page">
      {!embedded && (
        <PageHeader
          title="Alertes de sécurité"
          subtitle="Activité suspecte détectée côté client (DevTools, scrapping, bots) + backend (brute-force, burst API)."
          icon={<Shield />}
          actions={<a href="/admin/security/audit" className="text-xs font-bold text-[#f7c935] hover:underline" data-testid="link-access-audit-page">Journal d'audit des accès →</a>}
        />
      )}

      {/* View switcher */}
      <div className="flex gap-1 bg-[#0b0811] rounded-lg border border-white/5 p-1 mb-4 w-fit">
        <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-md text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white"}`} data-testid="view-list">
          <List className="w-3.5 h-3.5" /> Liste
        </button>
        <button onClick={() => setView("grouped")} className={`px-3 py-1.5 rounded-md text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${view === "grouped" ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white"}`} data-testid="view-grouped">
          <Layers className="w-3.5 h-3.5" /> Groupées ({groups.length})
        </button>
        <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-md text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${view === "map" ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white"}`} data-testid="view-map">
          <Globe2 className="w-3.5 h-3.5" /> Carte mondiale
        </button>
      </div>

      {view === "map" && <SecurityGeoMap onCountryClick={(cc) => { setFilterCountry(cc); setView("list"); }} />}

      {view === "grouped" && (
        <div className="bg-[#0b0811] rounded-xl border border-white/5 overflow-hidden" data-testid="grouped-view">
          {groups.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
              Aucune alerte groupée active sur la dernière heure.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#14101c] text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left p-2.5">IP</th>
                  <th className="text-left p-2.5">Type</th>
                  <th className="text-right p-2.5">Total</th>
                  <th className="text-left p-2.5">Sévérités</th>
                  <th className="text-left p-2.5">Première / Dernière</th>
                  <th className="text-left p-2.5">Utilisateurs</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g, i) => {
                  const sev = SEVERITY_BADGES[g.max_severity] || SEVERITY_BADGES.medium;
                  return (
                    <tr key={`${g.ip}-${g.kind}-${i}`} className="border-t border-white/5 hover:bg-white/5" data-testid={`group-${g.ip}-${g.kind}`}>
                      <td className="p-2.5 text-white font-mono">{g.ip || "—"}</td>
                      <td className="p-2.5"><span className="text-white">{KIND_LABELS[g.kind] || g.kind}</span></td>
                      <td className="p-2.5 text-right text-amber-300 font-bold text-base">{g.count}</td>
                      <td className="p-2.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${sev.bg}`} style={{ color: sev.color }}>{sev.label}</span>
                        <span className="ml-2 text-[10px] text-slate-500">
                          {Object.entries(g.by_severity).filter(([, n]) => n > 0).map(([k, n]) => `${k}:${n}`).join(" ")}
                        </span>
                      </td>
                      <td className="p-2.5 text-[10px] text-slate-500 font-mono">
                        {g.first_seen?.slice(11, 19)} → {g.last_seen?.slice(11, 19)}
                      </td>
                      <td className="p-2.5 text-slate-300">{g.user_emails?.slice(0, 2).join(", ")}{g.user_emails?.length > 2 ? ` +${g.user_emails.length - 2}` : ""}</td>
                      <td className="p-2.5">
                        <Button onClick={() => bulkResolveGroup(g.ip, g.kind)} variant="outline" size="sm" className="h-6 text-[10px] border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid={`bulk-resolve-${g.ip}`}>
                          Résoudre tout
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === "list" && (
      <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats?.total ?? "—"} testid="stat-total" />
        <Stat label="24h" value={stats?.last_24h ?? "—"} highlight testid="stat-24h" />
        <Stat label="7 jours" value={stats?.last_7d ?? "—"} testid="stat-7d" />
        <Stat label="Critiques" value={stats?.by_severity?.critical ?? 0} danger testid="stat-critical" />
      </div>

      {/* Filters */}
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-3 mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (email, kind, IP)…" className="pl-8 h-8 bg-[#14101c] border-white/10 text-white text-xs" data-testid="search-input" />
        </div>
        <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} className="h-8 bg-[#14101c] border border-white/10 text-white text-xs rounded px-2" data-testid="filter-kind">
          <option value="">Tous les kinds</option>
          {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className="h-8 bg-[#14101c] border border-white/10 text-white text-xs rounded px-2" data-testid="filter-severity">
          <option value="">Toutes sévérités</option>
          {Object.keys(SEVERITY_BADGES).map((k) => <option key={k} value={k}>{SEVERITY_BADGES[k].label}</option>)}
        </select>
        {filterCountry && (
          <span className="inline-flex items-center gap-1 h-8 px-2 rounded bg-violet-500/10 text-violet-300 text-[10px] font-bold">
            🌍 {filterCountry}
            <button onClick={() => setFilterCountry("")} className="text-violet-400 hover:text-violet-200 ml-1" data-testid="clear-country-filter">×</button>
          </span>
        )}
        <label className="inline-flex items-center gap-1.5 h-8 px-2 rounded bg-[#14101c] border border-white/10 text-[10px] text-slate-300 cursor-pointer">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="toggle-resolved" />
          Résolues
        </label>
        <Button onClick={reload} variant="outline" size="sm" className="h-8 text-xs" data-testid="refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Table */}
      <div className="bg-[#0b0811] rounded-xl border border-white/5 overflow-hidden" data-testid="alerts-table">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-slate-700" />
            Aucune alerte {filterKind || filterSeverity ? "pour ce filtre" : ""}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#14101c] text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-2.5">Date</th>
                <th className="text-left p-2.5">Kind</th>
                <th className="text-left p-2.5">Sévérité</th>
                <th className="text-left p-2.5">Utilisateur</th>
                <th className="text-left p-2.5">IP</th>
                <th className="text-left p-2.5">URL</th>
                <th className="p-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const sev = SEVERITY_BADGES[a.severity] || SEVERITY_BADGES.medium;
                const isResolved = a.resolved || a.dismissed;
                return (
                  <tr key={a.alert_id} className={`border-t border-white/5 hover:bg-white/5 ${isResolved ? "opacity-50" : ""}`} data-testid={`alert-row-${a.alert_id}`}>
                    <td className="p-2.5 text-slate-400 font-mono text-[10px]">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="p-2.5 text-white">{KIND_LABELS[a.kind] || a.kind}</td>
                    <td className="p-2.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${sev.bg}`} style={{ color: sev.color }}>{sev.label}</span>
                      {a.resolved && <span className="text-[9px] ml-1 px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold">RÉSOLUE</span>}
                      {a.dismissed && <span className="text-[9px] ml-1 px-1 py-0.5 rounded bg-slate-500/15 text-slate-400 font-bold">MASQUÉE</span>}
                    </td>
                    <td className="p-2.5 text-slate-300">{a.user_email || "—"}</td>
                    <td className="p-2.5 text-slate-400 font-mono text-[10px]">{a.ip || "—"}</td>
                    <td className="p-2.5 text-slate-400 truncate max-w-[200px]">{a.url || a.context?.url || "—"}</td>
                    <td className="p-2.5 whitespace-nowrap">
                      {!isResolved && (
                        <>
                          <button onClick={() => resolve(a.alert_id)} className="text-emerald-400 hover:text-emerald-300 p-1" title="Résoudre" data-testid={`resolve-${a.alert_id}`}>
                            <CheckCircle2 className="w-3 h-3" />
                          </button>
                          <button onClick={() => dismiss(a.alert_id)} className="text-slate-400 hover:text-slate-300 p-1" title="Masquer" data-testid={`dismiss-${a.alert_id}`}>
                            <EyeOff className="w-3 h-3" />
                          </button>
                        </>
                      )}
                      {isSuper && (
                        <button onClick={() => remove(a.alert_id)} className="text-rose-400 hover:text-rose-300 p-1" title="Supprimer" data-testid={`delete-${a.alert_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function Stat({ label, value, highlight, danger, testid }) {
  return (
    <div className={`bg-[#0b0811] rounded-xl border ${danger ? "border-rose-500/30" : highlight ? "border-amber-500/30" : "border-white/5"} p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${danger ? "text-rose-300" : highlight ? "text-amber-300" : "text-white"}`}>{value}</div>
    </div>
  );
}
